import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { DayClickEventHandler, isMatch } from 'react-day-picker';
import { addMonths, format, getHours, getMinutes, parse, set, subMonths } from 'date-fns';
import { Caption } from '../Caption';
import { DatePickerIcon } from '../DatePickerIcon';
import { Input } from '../../input/old';
import { getZIndex } from '../../../styles';
import { getDatePickerLocale } from '../DatePickerLocale';
import { Stack } from '../../stack';
import { Button } from '../../button/old';
import { DayPickerBase } from '../DayPickerBase';
import { TimePicker } from '../../time-picker/old';
import { useProviderConfig } from '../../provider';
import { useDatePickerAttributes } from '../hooks/useDatePickerAttributes';
import { date_picker_input_entered_disabled_style } from '../styles';
import { CalendarType, YearMonthCalendar } from './YearMonthCalendar';
import { checkInputValidation, convertStringToDate, getTime, isDisabledTime, isExactDateMatch } from './utils';
import { DATE_FORMAT, DATE_HOUR_MINUTE_FORMAT, DATE_HOUR_MINUTE_SECOND_FORMAT, DATE_MONTH_FORMAT } from '../constants';
import { getDisabledTimes, getTimePickerOptions } from '../../time-picker/utils';
import { FormHelperText } from '../../form';
import { DatePickerDisplay } from '../DatePickerDisplay';
import { DisabledTooltip } from '../../tooltip';
import { CaptionLabelType, DatePickerProps } from './types';
import { semantic_spacing } from '../../../foundation';
import { normalizeLegacyDisplayMode } from './displayMode';

const setDateToday = set(new Date(), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });

export const DatePickerOld = ({
  width = '100%',
  error,
  disabled,
  className,
  placeholder,
  size = 'medium',
  displayTimeFormat,
  value,
  disabledDays,
  showRemoveButton = false,
  showTodayButton = true,
  showTimePicker = false,
  showFooter = false,
  numberOfMonths: numberOfMonthsProps = 1,
  zIndex = getZIndex('picker'),
  displayMode = 'popover',
  unit,
  onChange,
  onSelect,
  opened: openProp,
  onOpen,
  onClose,
  timePickerProps,
}: DatePickerProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isBottomSheetPicker = displayMode === 'bottomSheet';
  const normalizedDisplayMode = normalizeLegacyDisplayMode(displayMode);
  const isUnControlled = showTimePicker || showFooter || isBottomSheetPicker || displayTimeFormat;
  const { locale } = useProviderConfig();
  const { cellSize, footer } = useDatePickerAttributes(normalizedDisplayMode);
  const [opened, setOpened] = useState<boolean>(openProp ?? false);
  const [showRemoveIcon, setShowRemoveIcon] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<Date | undefined>(value);
  const [currentValue, setCurrentValue] = useState<Date | undefined>(isUnControlled ? selectedDate : value);
  const [currentMonth, setCurrentMonth] = useState<Date | undefined>(currentValue);
  const [calendarType, setCalendarType] = useState<CalendarType>('day');
  const datePickerLocale = getDatePickerLocale(locale?.lang);
  const [inputValue, setInputValue] = useState<string>('');
  const [errorStatus, setErrorStatus] = useState<boolean | undefined>(error);

  const numberOfMonths = useMemo(() => {
    return isBottomSheetPicker || showTimePicker ? 1 : Math.max(numberOfMonthsProps, 1);
  }, [isBottomSheetPicker, showTimePicker, numberOfMonthsProps]);

  const disabledTime = useCallback(() => {
    return timePickerProps?.disabledTime
      ? timePickerProps.disabledTime(inputValue ? convertStringToDate(inputValue) : new Date())
      : null;
  }, [timePickerProps?.disabledTime, inputValue]);

  const { timeHourPickerOptions, timeMinutePickerOptions } = useMemo(() => {
    // @ts-expect-error: TS2345
    const disabledTimes = getDisabledTimes(disabledTime, inputValue ? convertStringToDate(inputValue) : new Date());

    return {
      timeHourPickerOptions: getTimePickerOptions(24, 1, disabledTimes.disabledHours),
      timeMinutePickerOptions: getTimePickerOptions(
        60,
        timePickerProps?.incrementMinute || 1,
        disabledTimes.disabledMinutes,
      ),
    };
  }, [disabledTime, timePickerProps, inputValue]);

  const dateFormat = useMemo(() => {
    if (unit === 'month') {
      return DATE_MONTH_FORMAT;
    }
    if (displayTimeFormat === 'hourMinute' || (showTimePicker && !displayTimeFormat)) {
      return DATE_HOUR_MINUTE_FORMAT;
    }
    if (displayTimeFormat === 'hourMinuteSecond') {
      return DATE_HOUR_MINUTE_SECOND_FORMAT;
    }

    return DATE_FORMAT;
  }, [displayTimeFormat, showTimePicker, unit]);

  const disabledSelectedTime = useMemo(() => {
    if (
      disabledTime === null ||
      (!showTimePicker && !inputValue && timeHourPickerOptions.length === 0 && timeMinutePickerOptions.length === 0) ||
      !checkInputValidation(inputValue, dateFormat, true)
    ) {
      return false;
    }

    return isDisabledTime(inputValue, timeHourPickerOptions, timeMinutePickerOptions);
  }, [disabledTime, showTimePicker, inputValue, timeHourPickerOptions, timeMinutePickerOptions]);

  const disabledMonth: ((date: Date) => boolean) | undefined = useMemo(() => {
    if (unit === 'month') {
      if (typeof disabledDays === 'function') {
        return disabledDays;
      }
      console.warn('unit이 month이면 disabledDays는 함수만 지원합니다.');
    }
  }, [disabledDays, unit]);

  const handleClose = () => {
    onClose?.();
    setOpened(false);
  };

  const handleInitial = (reset: boolean, close?: boolean) => {
    const initialDate = reset ? null : value;
    setErrorStatus(error);
    setCurrentMonth(currentValue);
    setSelectedDate(initialDate ?? undefined);
    setSelectedTime(initialDate ?? undefined);
    setInputValue('');
    if (close) {
      handleClose();
    }
    reset && onChange?.(initialDate ?? null);
  };

  useEffect(() => {
    if (!opened) {
      setCalendarType(unit === 'month' ? 'month' : 'day');
      setInputValue(value ? format(value, dateFormat) : '');

      inputRef.current?.blur();
    }
  }, [opened, unit]);

  const handleChangeDate = (day: Date | null) => {
    if (showTimePicker && selectedTime) {
      // @ts-expect-error: TS2345
      const newTimeDate = set(day, { hours: getHours(selectedTime), minutes: getMinutes(selectedTime) });
      handleChangeTime(newTimeDate);
      setInputValue(format(newTimeDate, dateFormat));
    } else {
      // @ts-expect-error: TS2345
      setInputValue(format(day, dateFormat));
    }

    if (typeof onSelect === 'function') {
      const selectValue =
        showTimePicker && selectedTime
          ? set(day ?? new Date(), { hours: getHours(selectedTime), minutes: getMinutes(selectedTime) })
          : day;
      onSelect(selectValue);
    }

    if (!isUnControlled) {
      onChange?.(day);
      handleClose();
    }
  };

  const handleChangeTime = (day: Date | null) => {
    // @ts-expect-error: TS2345
    const newTimeDate = set(selectedDate ?? setDateToday, { hours: getHours(day), minutes: getMinutes(day) });
    // 날짜 선택없이 시/분 선택한 경우 날짜는 오늘날짜로 설정합니다.
    !selectedDate && setSelectedDate(setDateToday);
    setSelectedTime(newTimeDate);
    setInputValue(format(newTimeDate, dateFormat));

    if (typeof onSelect === 'function') {
      onSelect(newTimeDate);
    }
  };

  const handelConfirm = () => {
    // 날짜만 선택한 경우 타임스탬프는 00:00:00 으로 전달합니다. timePicker를 통해 시/분정보를 입력한 경우 해당 선택된 날짜에 해당 시/분 정보와 함께 전달합니다.
    const changeValue = showTimePicker
      ? // @ts-expect-error: TS2345
        set(selectedDate, { hours: getHours(selectedTime), minutes: getMinutes(selectedTime) })
      : selectedDate;
    setInputValue('');
    // @ts-expect-error: TS2345
    onChange?.(changeValue);
    handleClose();
  };

  const calculateTimeForOnDayClick = (day: Date) => {
    const defaultTime = timePickerProps?.defaultTime;
    if (showTimePicker && timeMinutePickerOptions?.length > 0 && timeHourPickerOptions?.length > 0) {
      return set(day, {
        hours: getTime(timeHourPickerOptions, defaultTime?.hour),
        minutes: getTime(timeMinutePickerOptions, defaultTime?.minute),
      });
    } else {
      return day;
    }
  };

  useEffect(() => {
    setSelectedDate(value);
    setSelectedTime(value);
    setInputValue(value ? format(value, dateFormat) : '');
  }, [value]);

  useEffect(() => {
    setErrorStatus(error);
  }, [error]);

  // numberOfMonths가 3 이상인 경우, 현재 선택된 월을 가운데에 노출하기 위해 1-2개월을 당깁니다.
  const currentMonthShifted = useMemo(() => {
    return subMonths(currentMonth ?? new Date(), Math.floor((numberOfMonths - 1) / 2));
  }, [currentMonth, numberOfMonths]);

  const renderToYearMonthCalendar = () => (
    <YearMonthCalendar
      type={calendarType}
      displayMode={normalizedDisplayMode}
      selectedRange={{ from: currentValue, to: currentValue }}
      currentDate={currentMonth ?? new Date()}
      locale={datePickerLocale}
      disabledDates={disabledMonth}
      onChange={(date) => {
        if (unit === 'month') {
          onChange?.(date ?? null);
        } else {
          setCurrentMonth(date ?? undefined);
        }
      }}
      onClose={() => {
        if (unit === 'month') {
          setOpened(false);
        } else {
          setCalendarType('day');
        }
      }}
    />
  );

  const renderToCalendar = () => (
    <DayPickerBase
      cellSize={cellSize}
      displayMode={normalizedDisplayMode}
      locale={datePickerLocale}
      mode='single'
      selected={currentValue}
      className={classNames('pds-date-picker', className)}
      fixedWeeks
      showOutsideDays
      numberOfMonths={numberOfMonths}
      month={currentMonthShifted}
      onMonthChange={(shiftedMonth) => {
        setCurrentMonth(addMonths(shiftedMonth, Math.floor((numberOfMonths - 1) / 2)));
      }}
      disabled={disabledDays}
      onDayClick={(day, { disabled }): DayClickEventHandler | undefined => {
        if (disabled) {
          return;
        }
        handleChangeDate(calculateTimeForOnDayClick(day));
      }}
      components={{
        Caption: (props) => (
          <Caption
            {...props}
            onClickCaptionLabel={(type: CaptionLabelType) => setCalendarType(type)}
            displayMode={normalizedDisplayMode}
            currentMonth={currentMonthShifted}
            locale={datePickerLocale}
            multipleMonths={!isBottomSheetPicker && !(numberOfMonths === 1)}
          />
        ),
      }}
      footer={
        <Stack direction='column' mt={16} gap={16}>
          {showTimePicker &&
            (timePickerProps?.disabledTooltip && !selectedDate ? (
              <DisabledTooltip
                disabled={true}
                content={timePickerProps?.disabledTooltipText || locale?.TimePicker.disabledTooltipText}
                placement='top'
              >
                <TimePicker
                  {...timePickerProps}
                  readOnly={isBottomSheetPicker}
                  // @ts-expect-error: TS2322
                  disabledTime={disabledTime}
                  usePortal={false}
                  size='medium'
                  value={selectedTime}
                  fill={isBottomSheetPicker}
                  onChange={handleChangeTime}
                />
              </DisabledTooltip>
            ) : (
              <TimePicker
                {...timePickerProps}
                readOnly={isBottomSheetPicker}
                // @ts-expect-error: TS2322
                disabledTime={disabledTime}
                usePortal={false}
                size='medium'
                value={selectedTime}
                fill={isBottomSheetPicker}
                onChange={handleChangeTime}
              />
            ))}
          {isBottomSheetPicker && renderToNowButton()}
          {renderToTodayButton()}
        </Stack>
      }
    />
  );

  const renderToFooter = () => {
    const {
      container: { style, ...footerProps },
    } = footer;

    return (
      isUnControlled && (
        <Stack gap={8} {...(showFooter && { justify: 'flex-end' })} {...footerProps} css={style}>
          <Button {...footer.button} onClick={() => handleInitial(false, true)}>
            {locale?.DatePicker.footer.clear}
          </Button>
          <Button
            kind='primary'
            {...footer.button}
            disabled={!checkValidation(inputValue, true) || disabledSelectedTime}
            onClick={() => handelConfirm()}
          >
            {locale?.DatePicker.footer.ok}
          </Button>
        </Stack>
      )
    );
  };

  const renderToTodayButton = () => {
    if (!showTodayButton || showTimePicker) {
      return null;
    }

    return (
      <Button
        fill
        kind='outlined_primary'
        onClick={() => {
          handleChangeDate(setDateToday);
        }}
      >
        {locale?.DatePicker.footer.today}
      </Button>
    );
  };

  const renderToNowButton = () => {
    if (!showTimePicker || !showTodayButton) {
      return null;
    }

    return (
      <Button
        fill={isBottomSheetPicker}
        kind='outlined_primary'
        onClick={() => {
          const now = new Date();
          handleChangeDate(now);
          handleChangeTime(now);
        }}
      >
        {locale?.DatePicker.footer.now}
      </Button>
    );
  };

  const renderToTimePickerFooter = () => {
    return (
      <Stack justify={showTodayButton && showTimePicker ? 'space-between' : 'flex-end'}>
        {!isBottomSheetPicker && renderToNowButton()}
        {renderToFooter()}
      </Stack>
    );
  };

  /**
   * @param value
   * @param strongCheck value empty case, value.length 가 dateFormat 보다 적은 케이스 error 추가 판단
   */
  const checkValidation = (value: string, strongCheck = false) => {
    if (!checkInputValidation(value, dateFormat, strongCheck)) {
      return false;
    }

    if (disabledDays) {
      const disabledDayArrayList = Array.isArray(disabledDays) ? disabledDays : [disabledDays];
      const parsedDate = parse(value, dateFormat, new Date());
      return !isMatch(parsedDate, disabledDayArrayList);
    }

    return true;
  };

  useEffect(() => {
    if (inputValue) {
      const invalid = !checkValidation(inputValue);
      setInputValue(inputValue);
      setErrorStatus(error ?? invalid);

      if (checkValidation(inputValue, true)) {
        const day = convertStringToDate(inputValue);
        setSelectedDate(day);
        setCurrentMonth(day);
        setCurrentValue(day);
        showTimePicker && setSelectedTime(day);
      } else if (isExactDateMatch(inputValue, dateFormat)) {
        setCurrentMonth(convertStringToDate(inputValue));
      }
    }
  }, [inputValue, error]);

  const renderToInput = () => {
    return (
      <Stack
        direction='column'
        onMouseEnter={() => showRemoveButton && setShowRemoveIcon(true)}
        onMouseLeave={() => showRemoveButton && setShowRemoveIcon(false)}
        onKeyDown={(e) => e.key === 'Enter' && !opened && setOpened(true)}
        onClick={() => !disabled && setOpened(true)}
        gap={semantic_spacing.helper_text.gap.desktop}
      >
        <Input
          ref={inputRef}
          width={width}
          disabled={disabled}
          value={inputValue ?? ''}
          readOnly={isBottomSheetPicker || !opened}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && checkValidation(inputValue, true)) {
              handleChangeDate(convertStringToDate(inputValue));
            }
          }}
          onChange={(e) => setInputValue(e.target.value)}
          status={errorStatus || disabledSelectedTime ? 'error' : 'info'}
          size={size}
          placeholder={placeholder ?? locale?.DatePicker.placeholder}
          endElement={<DatePickerIcon showRemoveIcon={value && showRemoveIcon} onClick={() => handleInitial(true)} />}
          className={classNames('pds-date-picker-input', className)}
          css={disabled && value && date_picker_input_entered_disabled_style}
          data-testid='pds-date-picker-input'
        />
        {disabledSelectedTime && <FormHelperText status='error'>다른 시간을 선택해주세요.</FormHelperText>}
      </Stack>
    );
  };

  return (
    <DatePickerDisplay
      displayMode={normalizedDisplayMode}
      className={className}
      disabled={disabled}
      zIndex={zIndex}
      input={renderToInput()}
      content={calendarType === 'day' ? renderToCalendar() : renderToYearMonthCalendar()}
      footer={showTimePicker ? renderToTimePickerFooter() : renderToFooter()}
      opened={opened}
      onOpen={() => {
        setOpened(true);
        onOpen?.();
      }}
      onClose={() => {
        setOpened(false);
        onClose?.();
      }}
      onDismiss={() => {
        handleInitial(false);
      }}
    />
  );
};
